import React, { useState, KeyboardEvent, useRef, useEffect } from 'react';
import { Box, TextField, IconButton, Paper, Typography, Chip, Tooltip } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  ArrowUpward,
  Stop,
  Mic,
  StopCircle,
  AttachFile,
  Close,
  Image as ImageIcon,
  PictureAsPdf,
  AudioFile,
  Description,
} from '@mui/icons-material';
import { useLiveVoiceTranscription } from '@/components/chat/hooks/useLiveVoiceTranscription';
import {
  ATTACHMENT_ACCEPT,
  MAX_FILES_PER_SUBMIT,
  validateFile,
  type AttachmentKind,
} from './utils/chatMedia';

interface StagedAttachment {
  id: string;
  file: File;
  kind: AttachmentKind;
  previewUrl?: string;
}

interface ChatInputProps {
  onSend: (message: string, files?: File[]) => void;
  onCancel?: () => void;
  disabled?: boolean;
  isStreaming?: boolean;
  showError?: (message: string) => void;
}

function kindIcon(kind: AttachmentKind) {
  if (kind === 'image') return <ImageIcon sx={{ fontSize: 16 }} />;
  if (kind === 'pdf') return <PictureAsPdf sx={{ fontSize: 16 }} />;
  if (kind === 'document') return <Description sx={{ fontSize: 16 }} />;
  return <AudioFile sx={{ fontSize: 16 }} />;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  onCancel,
  disabled,
  isStreaming,
  showError,
}) => {
  const theme = useTheme();
  const [value, setValue] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState('');
  const [staged, setStaged] = useState<StagedAttachment[]>([]);

  // Mirror `staged` into a ref so the unmount cleanup revokes the previews that
  // are actually staged at teardown, not the empty array captured at mount.
  const stagedRef = useRef(staged);
  useEffect(() => {
    stagedRef.current = staged;
  }, [staged]);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const deepgramConnectionRef = useRef<any>(null);
  const finalTranscriptRef = useRef<string>('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { getDeepgramSocketConnection } = useLiveVoiceTranscription();

  // Release any object URLs still staged when the component unmounts.
  useEffect(() => {
    return () => {
      stagedRef.current.forEach((s) => {
        if (s.previewUrl) URL.revokeObjectURL(s.previewUrl);
      });
    };
  }, []);

  const handleFilesPicked = (filesList: FileList | null) => {
    if (!filesList || filesList.length === 0) return;
    const incoming = Array.from(filesList);

    const remainingSlots = MAX_FILES_PER_SUBMIT - staged.length;
    if (remainingSlots <= 0) {
      showError?.(`Maximum ${MAX_FILES_PER_SUBMIT} attachments per message.`);
      return;
    }

    const accepted: StagedAttachment[] = [];
    const errors: string[] = [];

    for (const file of incoming.slice(0, remainingSlots)) {
      const result = validateFile(file);
      if (!result.ok) {
        errors.push(result.error);
        continue;
      }
      const previewUrl = result.kind === 'image' ? URL.createObjectURL(file) : undefined;
      accepted.push({
        id: `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        kind: result.kind,
        previewUrl,
      });
    }

    if (incoming.length > remainingSlots) {
      errors.push(`Only ${remainingSlots} more file(s) allowed; extras were ignored.`);
    }

    if (accepted.length > 0) setStaged((prev) => [...prev, ...accepted]);
    if (errors.length > 0 && showError) showError(errors.join(' '));
  };

  const removeStaged = (id: string) => {
    setStaged((prev) => {
      const removed = prev.find((s) => s.id === id);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((s) => s.id !== id);
    });
  };

  const startRecording = async () => {
    try {
      finalTranscriptRef.current = '';
      setLiveTranscript('');

      // Buffer chunks that arrive before the WebSocket is ready
      const pendingBlobs: Blob[] = [];
      let socketReady = false;

      let isConnectionOpen = false;
      try {
        isConnectionOpen = !!deepgramConnectionRef.current &&
          deepgramConnectionRef.current.getReadyState() === WebSocket.OPEN;
      } catch {
        // Stale connection — close and discard
        try { deepgramConnectionRef.current?.close(); } catch { /* ignore */ }
        deepgramConnectionRef.current = null;
      }

      // Kick off mic request; only fetch a new Deepgram connection if needed
      const streamPromise = navigator.mediaDevices.getUserMedia({ audio: true });
      const connectionPromise = isConnectionOpen
        ? Promise.resolve(deepgramConnectionRef.current)
        : getDeepgramSocketConnection();

      const [stream, connection] = await Promise.all([streamPromise, connectionPromise]);

      if (!isConnectionOpen) {
        deepgramConnectionRef.current = connection;

        // Register the message listener once per connection lifetime
        connection.on('message', (data: any) => {
          if (data.type === 'Results') {
            const transcript = data.channel?.alternatives?.[0]?.transcript ?? '';
            if (transcript) {
              if (data.is_final) {
                finalTranscriptRef.current += transcript + ' ';
                setLiveTranscript(finalTranscriptRef.current);
              } else {
                setLiveTranscript(finalTranscriptRef.current + transcript);
              }
            }
          }
        });

        connection.connect();
        await connection.waitForOpen();
      }

      // Connection is ready — flush any buffered blobs immediately after recording starts
      socketReady = true;

      const mimeType = 'audio/webm;codecs=opus';
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          if (socketReady && deepgramConnectionRef.current) {
            deepgramConnectionRef.current.socket.send(event.data);
          } else {
            pendingBlobs.push(event.data);
          }
        }
      };

      mediaRecorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop());
      };

      // Start capturing immediately
      mediaRecorder.start(250);
      setIsRecording(true);

      for (const blob of pendingBlobs) {
        deepgramConnectionRef.current!.socket.send(blob);
      }
      pendingBlobs.length = 0;

      timeoutRef.current = setTimeout(() => {
        stopRecording();
      }, 60000); // 1 minute timeout

    } catch (err) {
      console.error('Error accessing microphone:', err);
      setIsRecording(false);
      setLiveTranscript('');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    mediaRecorderRef.current = null;
    setIsRecording(false);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const transcript = finalTranscriptRef.current.trim();
    if (transcript) {
      setValue(prev => prev ? `${prev} ${transcript}` : transcript);
    }
    setLiveTranscript('');
    finalTranscriptRef.current = '';
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      if (deepgramConnectionRef.current) {
        deepgramConnectionRef.current.close();
      }
    };
  }, []);

  const hasContent = value.trim().length > 0 || staged.length > 0;

  const handleSend = () => {
    if (!hasContent || disabled) return;
    onSend(value, staged.map((s) => s.file));
    setValue('');
    // useChatAgent creates its own object URLs from the underlying Files, so the
    // local previews are no longer needed once the message has been handed off.
    staged.forEach((s) => {
      if (s.previewUrl) URL.revokeObjectURL(s.previewUrl);
    });
    setStaged([]);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: alpha(theme.palette.background.default, 0.8),
        backdropFilter: 'blur(8px)',
      }}
    >
      <Paper
        elevation={2}
        sx={{
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          border: '1px solid',
          borderColor: alpha(theme.palette.divider, 0.1),
        }}
      >
        {isRecording && (
          <Box sx={{ px: 2, pt: 1.5 }}>
            <Typography
              variant="caption"
              sx={{ color: 'error.main', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              ● Recording
            </Typography>
            {liveTranscript && (
              <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', mt: 0.5 }}>
                {liveTranscript}
              </Typography>
            )}
          </Box>
        )}

        {staged.length > 0 && (
          <Box sx={{ px: 1.5, pt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {staged.map((s) => (
              <Chip
                key={s.id}
                icon={
                  s.previewUrl ? (
                    <Box
                      component="img"
                      src={s.previewUrl}
                      alt=""
                      sx={{ width: 20, height: 20, borderRadius: 0.5, objectFit: 'cover', ml: 0.5 }}
                    />
                  ) : (
                    kindIcon(s.kind)
                  )
                }
                label={s.file.name}
                onDelete={() => removeStaged(s.id)}
                deleteIcon={<Close fontSize="small" />}
                size="small"
                sx={{
                  maxWidth: 220,
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
            ))}
          </Box>
        )}

        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder="Type a message..."
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled || isRecording}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              pr: 1,
              fontSize: '0.9rem',
              '& fieldset': {
                border: 'none',
              },
            },
            '& .MuiInputBase-input': {
              py: 1.5,
              px: 2,
            },
          }}
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            px: 1.5,
            py: 1,
            bgcolor: 'background.paper',
            gap: 1,
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ATTACHMENT_ACCEPT}
            multiple
            hidden
            onChange={(e) => {
              handleFilesPicked(e.target.files);
              // Allow re-selecting the same file later.
              e.target.value = '';
            }}
          />

          <Tooltip title="Attach images, PDFs, or audio">
            <span>
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled || isStreaming || isRecording || staged.length >= MAX_FILES_PER_SUBMIT}
                sx={{
                  color: 'primary.main',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  border: '1px solid',
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                  },
                }}
              >
                <AttachFile fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <IconButton
            onClick={isRecording ? stopRecording : startRecording}
            disabled={disabled || isStreaming}
            sx={{
              color: isRecording ? 'white' : 'primary.main',
              bgcolor: isRecording ? 'error.main' : alpha(theme.palette.primary.main, 0.1),
              border: '1px solid',
              borderColor: isRecording ? 'error.main' : alpha(theme.palette.primary.main, 0.3),
              '&:hover': {
                bgcolor: isRecording
                  ? alpha(theme.palette.error.main, 0.85)
                  : alpha(theme.palette.primary.main, 0.2),
              },
              ...(isRecording && {
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0.4)}` },
                  '50%': { boxShadow: `0 0 0 8px ${alpha(theme.palette.error.main, 0)}` },
                },
              }),
            }}
          >
            {isRecording ? <StopCircle /> : <Mic />}
          </IconButton>

          {isStreaming && onCancel ? (
            <IconButton
              onClick={onCancel}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.error.main, 0.1),
                color: 'error.main',
                '&:hover': {
                  bgcolor: alpha(theme.palette.error.main, 0.2),
                },
              }}
            >
              <Stop fontSize="small" />
            </IconButton>
          ) : (
            <IconButton
              onClick={handleSend}
              disabled={!hasContent || disabled || isRecording}
              sx={{
                bgcolor: '#cc5544',
                color: 'white',
                width: 32,
                height: 32,
                '&:hover': {
                  bgcolor: '#b54636',
                },
                '&.Mui-disabled': {
                  bgcolor: alpha('#cc5544', 0.3),
                  color: 'white',
                },
              }}
            >
              <ArrowUpward fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Paper>
    </Box>
  );
};
